import AddIcon from "@mui/icons-material/Add";
import CreateIcon from "@mui/icons-material/Create";
import DeleteIcon from "@mui/icons-material/Delete";
import {Theme} from "@mui/material/styles";
import {createStyles, makeStyles} from "@mui/styles";
import {GridColDef, GridValueFormatterParams} from "@mui/x-data-grid";
import React, {useState} from "react";
import {CollectionDto} from "../../../../../common/apis/collection/CollectionDto";
import {CollectionPermissionLevelDto} from "../../../../../common/apis/user/CollectionPermissionLevelDto";
import {UserGroupCollectionPermissionDto} from "../../../../../common/apis/usergroup/UserGroupCollectionPermissionDto";
import Grid from "../../../../../components/dialogs/Grid";
import {ActionButtonType, EnabledPolicy, GridAction} from "../../../../../components/grid/GridAction";
import ExtGridWrapper from "../../../../../components/grid/ExtGridWrapper";
import SectionHeader from "../../../../../components/section/SectionHeader";
import {PersistentStateId} from "../../../../../store/common/Grid";
import {_transl, t} from "../../../../../store/localization/TranslMessasge";
import {UserGroupsTranslationKey} from "../UserGroupsTranslationKey";
import {useFindAllUserGroupPermissionsQuery} from "../UserGroupQuery";
import {ButtonId} from "../ButtonId";
import UserGroupPermissionCreateDialog from "./UserGroupPermissionCreateDialog";
import UserGroupPermissionDeleteDialog from "./UserGroupPermissionDeleteDialog";
import UserGroupPermissionUpdateDialog from "./UserGroupPermissionUpdateDialog";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        grid: {
            minHeight: "18em",
        }
    })
);

interface UserGroupPermissionPanelProps {
    groupId: string;
}

export default function UserGroupPermissionPanel({groupId}: Readonly<UserGroupPermissionPanelProps>) {
    const classes = useStyles();
    const {data: permissions} = useFindAllUserGroupPermissionsQuery(groupId);
    const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);

    const [selectedCollectionCode, setSelectedCollectionCode] = useState<string | undefined>();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);

    const columns: GridColDef<UserGroupCollectionPermissionDto>[] = [
        {
            field: 'collection',
            headerName: _transl(UserGroupsTranslationKey.PERMISSION_COLLECTION),
            width: 300,
            valueFormatter: (params: GridValueFormatterParams) =>
                params.value != null ? (params.value as CollectionDto).name : "",
        },
        {
            field: 'permissionLevel',
            headerName: _transl(UserGroupsTranslationKey.PERMISSION_LEVEL),
            width: 200,
            valueFormatter: (params: GridValueFormatterParams) =>
                params.value != null ? _transl((params.value as CollectionPermissionLevelDto).translationKey) : "",
        },
    ];

    return (
            <>
                <Grid container>
                    <Grid item xs={12}>
                        <SectionHeader title={_transl(UserGroupsTranslationKey.PERMISSIONS)}/>
                    </Grid>
                    <Grid item xs={12}>
                        <ExtGridWrapper
                                columns={columns}
                                rows={permissions ?? []}
                                rowCount={permissions?.length ?? 0}
                                getRowId={row => row.collection.code}
                                onRowClick={(params) => setSelectedCollectionCode(params.row.collection.code)}
                                dataGridClass={classes.grid}
                                actions={[
                                    GridAction.buttonBuilder("USER_GROUP_ADD_PERMISSION_BUTTON", ActionButtonType.IMMEDIATE, _transl(UserGroupsTranslationKey.ADD_PERMISSION),
                                        <AddIcon/>)
                                        .enabledPolicy(EnabledPolicy.ALWAYS)
                                        .onClick(() => setCreateDialogOpen(true))
                                        .build(),
                                    GridAction.buttonBuilder("USER_GROUP_UPDATE_PERMISSION_BUTTON", ActionButtonType.IMMEDIATE, _transl(UserGroupsTranslationKey.UPDATE_PERMISSION),
                                        <CreateIcon/>)
                                        .enabledPolicy(EnabledPolicy.WHEN_EXACTLY_ONE_SELECTED)
                                        .onClick((selectedRowIds) => {
                                            setSelectedCollectionCode(selectedRowIds[0] as string);
                                            setUpdateDialogOpen(true);
                                        })
                                        .build(),
                                    GridAction.buttonBuilder(ButtonId.DELETE_PERMISSION, ActionButtonType.IMMEDIATE, t(UserGroupsTranslationKey.DELETE_PERMISSION), <DeleteIcon/>)
                                            .enabledPolicy(EnabledPolicy.WHEN_EXACTLY_ONE_SELECTED)
                                            .onClick((selectedRowIds) => {
                                                setSelectedCollectionCode(selectedRowIds[0] as string);
                                                setDeleteDialogOpen(true);
                                            })
                                            .build(),
                                ]}
                                peristentStateId={PersistentStateId.USER_GROUP_DETAIL_PERMISSIONS_GRID}
                                resourceId={"userGroupPermissions"}
                        />
                    </Grid>
                </Grid>

                <UserGroupPermissionCreateDialog
                        open={createDialogOpen}
                        groupId={groupId}
                        onClose={() => setCreateDialogOpen(false)}/>

                {selectedCollectionCode &&
                        <UserGroupPermissionDeleteDialog
                                open={deleteDialogOpen}
                                groupId={groupId}
                                collectionCode={selectedCollectionCode}
                                onClose={() => setDeleteDialogOpen(false)}/>
                }

                {selectedCollectionCode &&
                        <UserGroupPermissionUpdateDialog
                                open={updateDialogOpen}
                                groupId={groupId}
                                permission={permissions?.find(p => p.collection.code === selectedCollectionCode)}
                                onClose={() => setUpdateDialogOpen(false)}/>
                }
            </>
    );
}
